import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { formatTime } from '../../../core/utils/date';
import { useAuthorNames, useProjects } from '../../../global/contexts/lookups/lookups';
import { useLivePulse } from '../contexts/live';
import Agents from './agents';
import Flow from './flow';
import { STREAM_STATUS, isTaskKind, kindLabel } from '../utils/notice';
import { AUTHOR_FILTERS, ORDERS, filterMovements, kindsPresent } from '../utils/movements';

import '../styles/live.css';

// The live board (T15.6).
//
// The state is asked for once over HTTP and everything after that arrives
// through the channel. What is on screen is therefore always one of two
// things, and the screen has to say which: what the channel pushed, or what
// the last HTTP answer left. A pulse that has gone quiet because the
// connection died looks exactly like a pulse that is quiet because nothing is
// happening — that ambiguity is the bug this header exists to close.

const CHANNEL_TEXT = {
    [STREAM_STATUS.idle]:       'Canal detenido',
    [STREAM_STATUS.connecting]: 'Conectando…',
    [STREAM_STATUS.live]:       'Canal en vivo',
    [STREAM_STATUS.retrying]:   'Reconectando…',
    [STREAM_STATUS.down]:       'Canal caído',
};

export default function LiveBoard() {
    const [params, setParams] = useSearchParams();
    const projectId = params.get('project') || '';

    const { rows, total, counts, applied, isLoading, error, updatedAt, recount, stream, reconnect } =
        useLivePulse({ projectId });

    // LOS FILTROS DEL HISTORIAL VIVEN EN LA URL, como el proyecto: una pantalla
    // en vivo que alguien esta mirando se pasa con un enlace, y el filtro es
    // parte de lo que estaba mirando.
    //
    // Y TAMIZAN LO QUE YA LLEGO, no lo que el canal trae: el pulso guarda las
    // ultimas 50 filas y eso es lo que hay. Por eso el renglon dice cuantas se
    // muestran de cuantas, en vez de dejar creer que son todas.
    const kindFilter = params.get('tipo') || '';
    const authorFilter = params.get('quien') || '';
    const order = params.get('orden') === 'viejos' ? 'viejos' : 'nuevos';

    const kinds = useMemo(() => kindsPresent(rows), [rows]);
    const movements = useMemo(
        () => filterMovements(rows, { kind: kindFilter, author: authorFilter, order }),
        [rows, kindFilter, authorFilter, order],
    );
    const sifted = kindFilter !== '' || authorFilter !== '' || order !== 'nuevos';
    const { userName, tokenName, tokenKind } = useAuthorNames();
    const { data: projects } = useProjects();

    const setFilter = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        setParams(target, { replace: true });
    };

    const clearFilters = () => {
        const target = new URLSearchParams(params);
        for (const key of ['tipo', 'quien', 'orden']) target.delete(key);
        setParams(target, { replace: true });
    };

    const setProject = (value) => {
        const target = new URLSearchParams(params);
        if (value) target.set('project', value);
        else target.delete('project');
        setParams(target);
    };

    const onReconnect = async () => {
        try {
            await reconnect();
        } catch {
            // reconnect() reloads over HTTP first, and that failure is already
            // on screen through the query's own error. Swallowing it here only
            // keeps the click from becoming an unhandled rejection.
        }
    };

    if (error) {
        return (
            <p className="pulse__error">
                {error.message || 'No se pudo cargar el pulso del trabajo'}
            </p>
        );
    }

    return (
        <section className="pulse">
            <header className="pulse__header">
                <div className="pulse__heading">
                    <span className="pulse__hint">En vivo</span>
                    <h2 className="pulse__title">Pulso</h2>
                </div>

                <div className="pulse__tools">
                    <select
                        className="pulse__filter"
                        value={projectId}
                        onChange={e => setProject(e.target.value)}
                        aria-label="Filtrar por proyecto"
                    >
                        <option value="">Todos los proyectos</option>
                        {(projects ?? []).map(project => (
                            <option key={project.id} value={project.id}>{project.name}</option>
                        ))}
                    </select>

                    <Channel stream={stream} onReconnect={onReconnect} />
                </div>
            </header>

            {/* Who is working, first. On a live board that is the question
                asked before any number, and the numbers below answer the
                second one. */}
            <Agents projectId={projectId} />

            {/* The graph is the state; the list under it is the evidence. Both
                come from the same two answers — the counts per state and the
                event log — so they cannot tell different stories. */}
            <Flow rows={rows} counts={counts} />

            {/* The screen keeps serving with the channel down, and says what it
                is serving: the hour of the last answer it got over HTTP. */}
            <p className="pulse__state">
                {isLoading
                    ? 'Pidiendo el estado…'
                    : <>Contado a las <strong>{formatTime(updatedAt)}</strong> · {total} movimientos registrados</>}
                {applied > 0 && (
                    <>
                        {' '}· {applied} {applied === 1 ? 'aviso movió' : 'avisos movieron'} los números{' '}
                        <button type="button" className="pulse__recount" onClick={recount}>
                            volver a contar
                        </button>
                    </>
                )}
                {stream.status === STREAM_STATUS.down && (
                    <span className="pulse__state-warn">
                        {' '}· sin canal, esto no se actualiza solo
                    </span>
                )}
            </p>

            {!isLoading && !rows.length && (
                <p className="pulse__empty">Todavía no se movió nada.</p>
            )}

            {rows.length > 0 && (
                <div className="pulse__sift">
                    <label className="pulse__sift-field">
                        <span>Tipo</span>
                        <select
                            className="pulse__filter"
                            value={kindFilter}
                            onChange={e => setFilter('tipo', e.target.value)}
                        >
                            <option value="">Todos los movimientos</option>
                            <option value="task">Solo de tareas</option>
                            {/* Solo los tipos que estan EN LO CARGADO: ofrecer
                                uno que no esta es ofrecer un filtro que solo
                                puede dar vacio. */}
                            <optgroup label="Un movimiento">
                                {kinds.map(kind => (
                                    <option key={kind.kind} value={kind.kind}>
                                        {kind.label} · {kind.count}
                                    </option>
                                ))}
                            </optgroup>
                        </select>
                    </label>

                    <label className="pulse__sift-field">
                        <span>Quién</span>
                        <select
                            className="pulse__filter"
                            value={authorFilter}
                            onChange={e => setFilter('quien', e.target.value)}
                        >
                            {AUTHOR_FILTERS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </label>

                    <label className="pulse__sift-field">
                        <span>Orden</span>
                        <select
                            className="pulse__filter"
                            value={order}
                            onChange={e => setFilter('orden', e.target.value === 'nuevos' ? '' : e.target.value)}
                        >
                            {ORDERS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </label>

                    <span className="pulse__sift-count">
                        {movements.length} de {rows.length} cargados
                        {total > rows.length && ` · ${total} registrados`}
                    </span>

                    {sifted && (
                        <button type="button" className="pulse__sift-clear" onClick={clearFilters}>
                            Limpiar
                        </button>
                    )}
                </div>
            )}

            {rows.length > 0 && movements.length === 0 && (
                <p className="pulse__empty">
                    Ninguno de los {rows.length} movimientos cargados coincide con este filtro.
                </p>
            )}

            <ul className="pulse__list">
                {movements.map(row => (
                    <li key={row.id} className={`movement${row.live ? ' movement--live' : ''}`}>
                        <span className="movement__time">{formatTime(row.at)}</span>

                        <span className={`movement__kind${isTaskKind(row.kind) ? ' movement__kind--task' : ''}`}>
                            {kindLabel(row.kind)}
                        </span>

                        <span className="movement__what">
                            {row.taskId
                                ? (
                                    <Link to={`/admin/tasks/${row.taskId}`} className="movement__task">
                                        <span className="movement__task-id">#{row.taskId}</span>
                                        {/* The title travels in the notice and not in the
                                            event row, so a movement that arrived over HTTP
                                            shows its number and nothing else. */}
                                        {row.taskTitle && (
                                            <span className="movement__task-title">{row.taskTitle}</span>
                                        )}
                                    </Link>
                                )
                                : <span className="movement__task-none">—</span>}
                        </span>

                        <Author
                            row={row}
                            userName={userName}
                            tokenName={tokenName}
                            tokenKind={tokenKind}
                        />
                    </li>
                ))}
            </ul>

            <footer className="pulse__foot">
                <p className="pulse__note">
                    El estado se pide una vez y después solo se escucha: cada movimiento que
                    ves llegar entró por el canal, no por una consulta repetida. Cada arista
                    del dibujo es un tipo de aviso, así que lo que se enciende es lo que
                    llegó — el dibujo no puede moverse sin que haya pasado algo.
                </p>
            </footer>
        </section>
    );
}

function Channel({ stream, onReconnect }) {
    const { status, attempts, lastEventId } = stream;

    return (
        <div className="channel" role="status" aria-live="polite">
            <span className={`channel__dot channel__dot--${status}`} />
            <span className="channel__text">
                {CHANNEL_TEXT[status] ?? status}
                {status === STREAM_STATUS.retrying && attempts > 0 && ` (intento ${attempts})`}
            </span>

            {/* The resume point, shown because it is the thing nobody can check
                otherwise: if it moves, the channel is replaying what it missed. */}
            {lastEventId && (
                <span className="channel__resume" title="Último evento recibido (Last-Event-ID)">
                    #{lastEventId}
                </span>
            )}

            {stream.error && <span className="channel__warn">{stream.error}</span>}

            {status === STREAM_STATUS.down && (
                <button type="button" className="channel__retry" onClick={onReconnect}>
                    Reconectar
                </button>
            )}
        </div>
    );
}

function Author({ row, userName, tokenName, tokenKind }) {
    if (row.tokenId != null) {
        const kind = tokenKind(row.tokenId);
        return (
            <span className="movement__who" title={userName(row.userId) ?? undefined}>
                {tokenName(row.tokenId)}
                {kind && <span className="movement__kindtag">{kind}</span>}
            </span>
        );
    }

    if (row.userId != null) {
        return <span className="movement__who">{userName(row.userId)}</span>;
    }

    // The notice travels without its author and it is asked for by id, so a
    // fresh arrival spends a moment here. "Asking" and "nobody" are different
    // answers and they read differently.
    if (row.authorPending) {
        return (
            <span className="movement__who movement__who--unknown" title="Pidiendo quién lo hizo">
                …
            </span>
        );
    }

    return (
        <span
            className="movement__who movement__who--unknown"
            title={row.live
                ? 'No se pudo saber quién lo hizo'
                : 'El evento no registró autor'}
        >
            —
        </span>
    );
}
